import * as _ from 'lodash';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../../shared/helpers';
import { useAddToast } from '../../../../contexts/ToastContext';
import { window } from '../../../../globals';
import { errorMessagesListFromResponse } from '../../../../helpers/errors';
import { useAsyncValue } from '../../../../hooks';
import { patch } from '../../../../redux/api';
import { LabeledCheckbox } from '../../../generic/Forms';
import SettingsPropagateModal from '../../shared/SettingsPropagateModal';
import type {
  ProjectUpdateHooksSettings,
  ProjectUpdateHooksSettingsFields,
} from '../../../../../shared/settings/project/update-hooks';
import type { FailedFetchResponse } from '../../../../helpers/errors';

type Props = {
  isOpen: boolean;
  settings: ProjectUpdateHooksSettings
  onHide: () => void;
};

//  All the propagate checkboxes are disabled by default
const resetCheckboxState: Record<ProjectUpdateHooksSettingsFields, boolean> = {
  updateHookDefaultScheduledEnabled: false,
  updateHookDefaultScheduledInterval: false,
  updateHookDefaultScheduledIntervalLength: false,
  updateHookDefaultScheduledIntervalTime: false,
};

/**
 * Hook for managing the state of the checkboxes and making the PATCH request.
 */
function usePropagateProjectUpdateHooksSettings({
  isOpen,
  onHide,
}: Omit<Props, 'settings'>) {
  const addToast = useAddToast();
  //  Void here because we don't really care about the stored value, we're just using this to track the status of the PATCH request.
  const asyncValue = useAsyncValue<void>();
  const [checkboxes, setCheckboxes] = useState<Record<ProjectUpdateHooksSettingsFields, boolean>>(
    resetCheckboxState,
  );

  //  Reset the checkboxes when the modal closes.
  //  We always want the user to select which options should propagate,
  //  which is why none should be checked when the user opens the modal
  useEffect(() => {
    setCheckboxes(resetCheckboxState);
  }, [isOpen]);

  const onSavePropagateSettings = useCallback(async () => {
    try {
      asyncValue.setPending();
      await patch<ProjectUpdateHooksSettingsFields[]>(
        `/api/organizations/${window.user?.organizationId}/settings/projects/update-hooks`,
        _.compact(
          Object.entries(checkboxes)
            .map(([key, value]) => (value ? (key as ProjectUpdateHooksSettingsFields) : null)),
        ),
      );
      asyncValue.setResolve();
      //  Show toast and hide the modal after we successfully propagate.
      addToast({ type: 'success', content: 'Settings successfully applied to all projects.' });
      onHide();
    } catch (error) {
      asyncValue.setReject(
        errorMessagesListFromResponse(error as FailedFetchResponse),
      );
    }
  }, [addToast, asyncValue, checkboxes, onHide]);

  return {
    checkboxes,
    setCheckboxes,
    onSavePropagateSettings,
    isLoading: asyncValue.isLoading,
    errors: asyncValue.errors,
    isSaveButtonDisabled: asyncValue.isLoading || _.isEqual(checkboxes, resetCheckboxState),
  };
}

/**
 * PropagateProjectUpdateHooksModal is the modal for propagating Update Hooks settings to all projects.
 */
const PropagateProjectUpdateHooksModal: VoidFunctionComponent<Props> = ({
  isOpen,
  settings,
  onHide,
}) => {
  const {
    checkboxes,
    setCheckboxes,
    onSavePropagateSettings,
    isLoading,
    errors,
    isSaveButtonDisabled,
  } = usePropagateProjectUpdateHooksSettings({ isOpen, onHide });
  return (
    <SettingsPropagateModal
      isOpen={isOpen}
      onHide={onHide}
      settingsCategory="update hooks"
      errors={errors}
      onSave={onSavePropagateSettings}
      isSaveButtonDisabled={isSaveButtonDisabled}
      isSaveButtonLoading={isLoading}
      //  Explain to the user exactly what happens. If it was confusing to us as we implemented it, it could be confusing to the user.
      description="Your changes to update hook settings will apply to all projects with existing update hooks. For projects without update hooks, your settings will be used to set them up. Which changes do you want to propagate to all projects?"
    >
      <LabeledCheckbox
        label={settings.updateHookDefaultScheduledEnabled ? 'Enable update hooks on all projects' : 'Disable update hooks on all projects'}
        isChecked={checkboxes.updateHookDefaultScheduledEnabled}
        onChange={(_event, isChecked) => setCheckboxes(currentCheckboxes => ({
          ...currentCheckboxes,
          updateHookDefaultScheduledEnabled: isChecked,
        }))}
        isRippleDisabled
      />
      <LabeledCheckbox
        label={!settings.updateHookDefaultScheduledInterval
          ? `Unset the default update cadence for all projects`
          : `Set the default update cadence to "${settings.updateHookDefaultScheduledInterval}" for all projects`}
        isChecked={checkboxes.updateHookDefaultScheduledInterval}
        onChange={(_event, isChecked) => setCheckboxes(currentCheckboxes => ({
          ...currentCheckboxes,
          updateHookDefaultScheduledInterval: isChecked,
        }))}
        isRippleDisabled
      />
      <LabeledCheckbox
        label={!settings.updateHookDefaultScheduledIntervalLength
          ? `Unset the default update hook interval length for all projects`
          : `Set the default update hook interval length to "${settings.updateHookDefaultScheduledIntervalLength} ${plural(settings.updateHookDefaultScheduledIntervalLength ?? 0, settings.updateHookDefaultScheduledInterval || '')}" for all projects`}
        isChecked={checkboxes.updateHookDefaultScheduledIntervalLength}
        onChange={(_event, isChecked) => setCheckboxes(currentCheckboxes => ({
          ...currentCheckboxes,
          updateHookDefaultScheduledIntervalLength: isChecked,
        }))}
        isRippleDisabled
      />
      <LabeledCheckbox
        label={!settings.updateHookDefaultScheduledIntervalTime
          ? `Unset the default update hook interval time for all projects`
          : `Set the default update hook interval time to "${moment(settings.updateHookDefaultScheduledIntervalTime, 'HH:mm:ss').format('h:mma')}" Pacific Time (PT) for all projects`}
        isChecked={checkboxes.updateHookDefaultScheduledIntervalTime}
        onChange={(_event, isChecked) => setCheckboxes(currentCheckboxes => ({
          ...currentCheckboxes,
          updateHookDefaultScheduledIntervalTime: isChecked,
        }))}
        isRippleDisabled
      />
    </SettingsPropagateModal>
  );
};

export default PropagateProjectUpdateHooksModal;
